import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Auth } from "aws-amplify";
import { toast } from "react-toastify";
import BaseAuthLayout from "../../hoc/BaseAuthLayout/BaseAuthLayout";
import "./Login.scss";
import memberSvg from "../../assets/images/member.svg";
import passLock from "../../assets/images/pass-lock.svg";
import eye from "../../assets/images/eye.svg";
import eyeSlash from "../../assets/images/eye-slash.svg";
import queryString from "query-string";

interface LoginProps {
  authenticate: () => void;
  isLoading: boolean;
}

const MFA_CHALLENGES = ["SOFTWARE_TOKEN_MFA"];

function Login(props: LoginProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const urlUsername = queryParams.username as string;
  const urlTempPassword = queryParams.tempPassword as string;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaChallengeName, setMfaChallengeName] = useState("");
  const [mfaUser, setMfaUser] = useState<any | null>(null);

  const clearMfaState = () => {
    setMfaCode("");
    setMfaChallengeName("");
    setMfaUser(null);
  };

  const completeLogin = async () => {
    props.authenticate();
    toast.dismiss();
    navigate("/");
  };

  const handleMfaSignIn = async () => {
    if (!/^\d{6}$/.test(mfaCode)) {
      toast.dismiss();
      toast.error("Ange en giltig 6-siffrig verifieringskod");
      return;
    }

    const challengeType =
      mfaChallengeName === "SMS_MFA" ? "SMS_MFA" : "SOFTWARE_TOKEN_MFA";

    await Auth.confirmSignIn(mfaUser, mfaCode, challengeType);
    clearMfaState();
    await completeLogin();
  };

  const handlePasswordSignIn = async () => {
    const usernameAllSmall = username.toLowerCase();
    const user = await Auth.signIn(usernameAllSmall, password);

    if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
      toast.dismiss();
      // user.username is Cognito's internal id after sign-in, not the address
      // the member typed - show them what they know.
      navigate(`/force-change-password`, { state: { email: usernameAllSmall } });
      return;
    }

    if (MFA_CHALLENGES.includes(user.challengeName)) {
      toast.dismiss();
      setMfaUser(user);
      setMfaChallengeName(user.challengeName);
      toast.info("Ange verifieringskoden från din autentiseringsapp");
      return;
    }

    await completeLogin();
  };

  const signIn = async (e: any) => {
    e.preventDefault();

    try {
      toast.loading("Loggar in...");
      if (mfaUser) {
        await handleMfaSignIn();
      } else {
        await handlePasswordSignIn();
      }
    } catch (error: any) {
      toast.dismiss();
      switch (error.code) {
        case "CodeMismatchException":
          toast.error("Fel verifieringskod");
          break;
        case "ExpiredCodeException":
          toast.error("Verifieringskoden har gått ut, försök igen");
          break;
        case "NotAuthorizedException":
          if (error.message === "User is disabled.") {
            toast.error(
              "Användaren är inaktiv. Kontakta kansliet för mer information.",
            );
          } else if (error.message === "Incorrect username or password.") {
            toast.error("Felaktigt användarnamn eller lösenord");
          } else if (
            error.message ===
            "Invalid session for the user, session is expired."
          ) {
            clearMfaState();
            toast.error(
              "Sessionen har gått ut. Logga in med användarnamn och lösenord igen.",
            );
          } else {
            toast.error(error.message);
          }
          break;
        default:
          toast.error(error.message);
          break;
      }
    }
  };

  useEffect(() => {
    if (urlUsername && urlTempPassword) {
      setUsername(urlUsername);
      setPassword(urlTempPassword);
    }
  }, [urlUsername, urlTempPassword]);

  function resetPassword() {
    navigate("/reset-password");
  }

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  useEffect(() => {
    const checkAuthState = async () => {
      try {
        await Auth.currentAuthenticatedUser();
        navigate("/");
      } catch (error) {
        console.error("Error during authentication check:", error);
      }
    };

    checkAuthState();
  }, []);
  return (
    <BaseAuthLayout header="Logga in">
      <div className="login-wrapper">
        <form className="login-form" onSubmit={signIn}>
          <div className="line-divider marg-30"></div>
          <div className="general-auth-input-wrapper">
            <div className="general-label-input">
              <label htmlFor="username" className="input-label">
                <img alt="member-icon" src={memberSvg} />
                <span className="text-s secondary-text">
                  Användarnamn (E-post)
                </span>
              </label>
              <input
                type="string"
                placeholder=""
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="general-label-input">
              <label htmlFor="password" className="input-label">
                <img alt="password-lock" src={passLock} />
                <span className="text-s secondary-text">Lösenord*</span>
              </label>
              <div className="password-input-wrapper">
                <input
                  type={passwordVisible ? "text" : "password"}
                  placeholder=""
                  autoComplete="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <img
                  alt="toggle password visibility"
                  src={passwordVisible ? eyeSlash : eye}
                  className="password-toggle-icon"
                  onClick={togglePasswordVisibility}
                />
              </div>
            </div>

            {mfaUser && (
              <div className="general-label-input">
                <label htmlFor="mfaCode" className="input-label">
                  <img alt="password-lock" src={passLock} />
                  <span className="text-s secondary-text">
                    {mfaChallengeName === "SMS_MFA"
                      ? "Verifieringskod från SMS*"
                      : "Verifieringskod från autentiseringsapp*"}
                  </span>
                </label>
                <input
                  type="text"
                  placeholder="000000"
                  id="mfaCode"
                  inputMode="numeric"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) =>
                    setMfaCode(e.target.value.replace(/\D/g, ""))
                  }
                />
              </div>
            )}
          </div>
          <div className="line-divider marg-30"></div>
          <div className="login-bottom-wrapper">
            <div className="text-s">
              {!mfaUser ? (
                <>
                  Har du glömt ditt lösenord?
                  <a onClick={resetPassword}> Återställ lösenord</a>
                </>
              ) : (
                <a
                  onClick={() => {
                    clearMfaState();
                    toast.dismiss();
                  }}
                >
                  Tillbaka till inloggning
                </a>
              )}
            </div>
            <button type="submit" className="wide text-m">
              {mfaUser ? "Verifiera och logga in" : "Logga In"}
            </button>
          </div>
        </form>
      </div>
    </BaseAuthLayout>
  );
}

export default Login;
